import { useMutation, useQuery } from 'react-query';
import { datadogLogs } from '@datadog/browser-logs';
import { IProduct } from '../../interfaces/product.interface';
import { baseApi, buildAuthHeaders, useRetrieveToken } from './shared';

export const useProductApi = () => {
  const { retrieveToken, isAuthenticated } = useRetrieveToken();

  const useProducts = () => {
    return useQuery<unknown, unknown, IProduct[], any>(
      ['all_products'],
      async ({ signal }) => {
        const token = await retrieveToken('getProducts');
        if (!token) return;

        const response = await fetch(`${baseApi}/products/active`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
          signal,
        });

        if (!response.ok) {
          datadogLogs.logger.error('Failed to fetch products', {
            name: 'getProducts',
            response: { status: response.status, statusText: response.statusText },
          });
          throw new Error('Failed to fetch products');
        }

        return response.json();
      },
      { refetchOnWindowFocus: false, refetchOnMount: false, cacheTime: 3600000, enabled: isAuthenticated },
    );
  };

  const useAllProducts = () => {
    return useQuery<unknown, unknown, IProduct[], any>(
      ['all_products'],
      async ({ signal }) => {
        const token = await retrieveToken('getAllProducts');
        if (!token) return;

        const response = await fetch(`${baseApi}/products/all`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
          signal,
        });

        if (!response.ok) {
          datadogLogs.logger.error('Failed to fetch products', {
            name: 'getAllProducts',
            response: { status: response.status, statusText: response.statusText },
          });
          throw new Error('Failed to fetch products');
        }

        return response.json();
      },
      { refetchOnWindowFocus: false, refetchOnMount: false, enabled: isAuthenticated },
    );
  };

  const createProduct = useMutation(async (productData: IProduct) => {
    const token = await retrieveToken('createProduct');
    if (!token) return;

    const response = await fetch(`${baseApi}/products`, {
      method: 'POST',
      headers: buildAuthHeaders(token),
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      datadogLogs.logger.error('Failed to create product', {
        name: 'createProduct',
        response: { status: response.status, statusText: response.statusText },
      });
      throw new Error('Failed to create product');
    }
  });

  const updateProduct = useMutation(async (productData: IProduct) => {
    const token = await retrieveToken('updateProduct');
    if (!token) return;

    const response = await fetch(`${baseApi}/products/${productData._id}`, {
      method: 'PUT',
      headers: buildAuthHeaders(token),
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      datadogLogs.logger.error('Failed to update product', {
        name: 'updateProduct',
        response: { status: response.status, statusText: response.statusText },
      });
      throw new Error('Failed to update product');
    }
  });

  const uploadFindOutMorePDF = useMutation(async ({ file, code }: { file: File; code: string }) => {
    const token = await retrieveToken('uploadFindOutMorePDF');
    if (!token) return;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${baseApi}/products/${code}/find-out-more`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      datadogLogs.logger.error('Failed to upload file', {
        name: 'uploadFindOutMorePDF',
        response: { status: response.status, statusText: response.statusText },
      });
      throw new Error('Failed to upload file');
    }

    return (await response.json()) as { publicUrl: string };
  });

  return {
    useProducts,
    useAllProducts,
    createProduct,
    updateProduct,
    uploadFindOutMorePDF,
  };
};
